import React from "react";
import {Helmet} from "react-helmet";
import {useLocation} from "react-router-dom";
import useTranslation from "../../utils/hooks/useTranslation";

export const SITE_NAME = "SSD - Security Solution & Distribution";
const DEFAULT_IMAGE = "/android-chrome-512x512.png";

// Collapses (possibly HTML) content into a single line of at most `length` chars,
// suitable for a meta description.
export function summarize(text, length = 160) {
    if (!text) return "";

    const plain = String(text)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    if (plain.length <= length) return plain;

    const cut = plain.slice(0, length - 1);
    const space = cut.lastIndexOf(" ");

    return (space > length / 2 ? cut.slice(0, space) : cut).replace(/[\s,.;:-]+$/, "") + "…";
}

function toAbsoluteUrl(url) {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;

    return window.location.origin + (url.startsWith("/") ? url : "/" + url);
}

/**
 * Per-page SEO tags: title, description, canonical URL, robots, Open Graph and Twitter card.
 */
export default function Seo({title, description, image, type = "website", noIndex = false, canonicalPath}) {
    const {t, language} = useTranslation();
    const location = useLocation();

    const pageTitle = title || SITE_NAME;
    const pageDescription = summarize(description || t("seo.home_description"));
    const path = (canonicalPath || location.pathname).replace(/\/+$/, "") || "/";
    const canonicalUrl = window.location.origin + path;
    const imageUrl = toAbsoluteUrl(image || DEFAULT_IMAGE);

    return (
        <Helmet htmlAttributes={{lang: language}}>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription}/>
            {noIndex && <meta name="robots" content="noindex, follow"/>}
            {!noIndex && <link rel="canonical" href={canonicalUrl}/>}

            <meta property="og:site_name" content={SITE_NAME}/>
            <meta property="og:type" content={type}/>
            <meta property="og:title" content={pageTitle}/>
            <meta property="og:description" content={pageDescription}/>
            <meta property="og:url" content={canonicalUrl}/>
            <meta property="og:image" content={imageUrl}/>
            <meta property="og:locale" content={language === "sq" ? "sq_AL" : "en_US"}/>

            <meta name="twitter:card" content="summary_large_image"/>
            <meta name="twitter:title" content={pageTitle}/>
            <meta name="twitter:description" content={pageDescription}/>
            <meta name="twitter:image" content={imageUrl}/>
        </Helmet>
    );
}
